import { useMemo, useState } from "react";
import { openWithCacheBuster } from "./cacheBuster";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import {
	getIssueTypeLabel,
	type IssueSeverity,
	type IssueType,
	type PageIssue,
} from "@/types/technicalAudit";

interface PageGroup {
	url: string;
	pageTitle: string | null;
	issues: PageIssue[];
	severityCounts: Record<IssueSeverity, number>;
	topIssueType: IssueType;
}

interface IssuesByPagePanelProps {
	issues: PageIssue[];
	issueTypeLabels: Record<string, string>;
	onIssueClick?: (issue: PageIssue) => void;
}

function severityBadgeClass(severity: string) {
	if (severity === "critical") return "bg-destructive/10 text-destructive";
	if (severity === "warning") return "bg-coral-light text-coral-dark";
	return "bg-teal-light text-teal-dark";
}

export function IssuesByPagePanel({
	issues,
	issueTypeLabels,
	onIssueClick,
}: IssuesByPagePanelProps) {
	const [expanded, setExpanded] = useState<Set<string>>(new Set());

	const pageGroups = useMemo<PageGroup[]>(() => {
		const map = new Map<string, PageIssue[]>();
		for (const issue of issues) {
			const list = map.get(issue.page_url) ?? [];
			list.push(issue);
			map.set(issue.page_url, list);
		}

		const groups: PageGroup[] = [];
		for (const [url, list] of map.entries()) {
			const sevCounts: Record<IssueSeverity, number> = {
				critical: 0,
				warning: 0,
				info: 0,
			};
			const typeCounts: Record<string, number> = {};
			let topType: IssueType = list[0].issue_type;
			let topCount = 0;
			for (const i of list) {
				const sev = i.severity || "warning";
				sevCounts[sev] += 1;
				typeCounts[i.issue_type] = (typeCounts[i.issue_type] || 0) + 1;
				if (typeCounts[i.issue_type] > topCount) {
					topCount = typeCounts[i.issue_type];
					topType = i.issue_type;
				}
			}
			groups.push({
				url,
				pageTitle: list[0].page_title,
				issues: list,
				severityCounts: sevCounts,
				topIssueType: topType,
			});
		}
		groups.sort((a, b) => b.issues.length - a.issues.length);
		return groups;
	}, [issues]);

	const toggle = (url: string) => {
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(url)) next.delete(url);
			else next.add(url);
			return next;
		});
	};

	if (pageGroups.length === 0) {
		return (
			<div className="p-12 text-center text-sm text-muted-foreground">
				No pages with issues match the current filters.
			</div>
		);
	}

	return (
		<div className="divide-y divide-border">
			<div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-muted text-xs font-medium uppercase tracking-wide text-muted-foreground">
				<div className="col-span-6">Page</div>
				<div className="col-span-1 text-center">Total</div>
				<div className="col-span-3">Severity</div>
				<div className="col-span-2">Top Issue</div>
			</div>
			{pageGroups.map((group) => {
				const isOpen = expanded.has(group.url);
				return (
					<div key={group.url}>
						<button
							type="button"
							onClick={() => toggle(group.url)}
							className="w-full grid grid-cols-12 gap-3 px-4 py-3 items-center text-left hover:bg-muted transition-colors"
						>
							<div className="col-span-6 flex items-center gap-2 min-w-0">
								{isOpen ? (
									<ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
								) : (
									<ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
								)}
								<div className="min-w-0">
									<div className="text-sm text-foreground truncate">
										{group.pageTitle || group.url}
									</div>
									<div className="text-xs text-muted-foreground truncate">
										{group.url}
									</div>
								</div>
							</div>
							<div className="col-span-1 text-center">
								<span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 text-sm font-semibold rounded-full bg-muted text-foreground">
									{group.issues.length}
								</span>
							</div>
							<div className="col-span-3 flex items-center gap-1.5">
								{group.severityCounts.critical > 0 && (
									<span
										className={cn(
											"px-1.5 py-0.5 rounded text-xs font-medium",
											severityBadgeClass("critical"),
										)}
									>
										{group.severityCounts.critical} critical
									</span>
								)}
								{group.severityCounts.warning > 0 && (
									<span
										className={cn(
											"px-1.5 py-0.5 rounded text-xs font-medium",
											severityBadgeClass("warning"),
										)}
									>
										{group.severityCounts.warning} warn
									</span>
								)}
								{group.severityCounts.info > 0 && (
									<span
										className={cn(
											"px-1.5 py-0.5 rounded text-xs font-medium",
											severityBadgeClass("info"),
										)}
									>
										{group.severityCounts.info} info
									</span>
								)}
							</div>
							<div className="col-span-2 text-sm text-muted-foreground truncate">
								{issueTypeLabels[group.topIssueType] || group.topIssueType}
							</div>
						</button>
						{isOpen && (
							<div className="bg-muted/60 px-4 py-3 border-t border-border">
								<div className="flex items-center justify-between mb-2">
									<a
										href={group.url}
										onClick={(e) => openWithCacheBuster(e, group.url)}
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center gap-1 text-xs text-coral hover:text-coral-hover"
									>
										Open page <ExternalLink className="w-3 h-3" />
									</a>
								</div>
								<div className="space-y-1">
									{group.issues.map((issue) => {
										const sev = issue.severity || "warning";
										return (
											<button
												key={issue.id}
												type="button"
												onClick={(e) => {
													e.stopPropagation();
													onIssueClick?.(issue);
												}}
												className="w-full flex items-center justify-between px-3 py-2 bg-card border border-border rounded-lg hover:border-coral hover:shadow-sm transition-all text-left"
											>
												<div className="flex items-center gap-3 min-w-0">
													<span
														className={cn(
															"px-1.5 py-0.5 rounded text-xs font-medium shrink-0",
															severityBadgeClass(sev),
														)}
													>
														{sev}
													</span>
													<span className="text-sm text-foreground truncate">
														{getIssueTypeLabel(issue)}
													</span>
												</div>
												<span className="text-xs text-muted-foreground capitalize shrink-0">
													{issue.fix_status}
												</span>
											</button>
										);
									})}
								</div>
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
}
